import { useEffect, useMemo } from 'react'
import { fetchRelatedTravelItems } from 'actions/OfferActions'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { EmptyArray } from 'lib/array/arrayUtils'
import type { OfferDetailBreadcrumb } from '../OfferDetail'

/**
 * Accommodation drawer breadcrumbs, read from the same redux slice the offer
 * pages fill (`fetchRelatedTravelItems` -> `state.offer.relatedTravelItems`).
 * Sharing the slice means a drawer opened for an offer whose page was already
 * visited renders straight from the store, and the trail matches the one the
 * page shows for the same property.
 *
 * The stored trail is `[vertical, ...locations, property]`, and only the
 * locations in the middle are places you can narrow to. The first of those is
 * the country, so every crumb below it can name one when it opens its listing
 * drawer -- the country crumb itself has nothing broader to qualify it.
 *
 * `breadcrumbsFetcher` asserts on `offer.property`, so an offer that arrives
 * without one is skipped here rather than throwing inside the thunk.
 */
function useDrawerHotelBreadcrumbs(
  offer: App.HotelOffer | App.BedbankOffer | undefined,
  checkIn?: string,
  checkOut?: string,
): Array<OfferDetailBreadcrumb> {
  const dispatch = useAppDispatch()
  const offerId = offer?.id
  const relatedTravelItems = useAppSelector((state) => (
    offerId ? state.offer.relatedTravelItems[offerId] : undefined
  ))

  useEffect(() => {
    if (!offer?.property) return
    dispatch(fetchRelatedTravelItems(offer, checkIn, checkOut))
  }, [checkIn, checkOut, dispatch, offer])

  return useMemo<Array<OfferDetailBreadcrumb>>(() => {
    if (!relatedTravelItems?.length) return EmptyArray

    const lastLocationIndex = relatedTravelItems.length - 2
    const countryName = relatedTravelItems[1]?.text

    return relatedTravelItems.map((item, index) => {
      const isLocation = index > 0 && index <= lastLocationIndex
      if (!isLocation) {
        return { text: item.text }
      }

      return {
        text: item.text,
        place: {
          name: item.text,
          country: index > 1 ? countryName : undefined,
        },
      }
    })
  }, [relatedTravelItems])
}

export default useDrawerHotelBreadcrumbs
